import "./timeline.scss";

import { GetActivityLogDto } from "@api/api";
import { Tag } from "primereact/tag";
import { Timeline } from "primereact/timeline";
import { useState } from "react";

export interface ActivityTimelineProps {
  data: GetActivityLogDto[];
  formatDateTime: (date: string) => string;
}

interface TimelineEvent extends GetActivityLogDto {
  icon: string;
  color: string;
}

export default function ActivityTimeline({
  data,
  formatDateTime,
}: ActivityTimelineProps) {
  const events: TimelineEvent[] = data.map((log) => {
    let icon = "pi pi-circle";
    let color = "#6b7280";

    switch (log.action) {
      case "CREATE":
        icon = "pi pi-plus";
        color = "#22c55e";
        break;
      case "UPDATE":
        icon = "pi pi-pencil";
        color = "#3b82f6";
        break;
      case "DELETE":
        icon = "pi pi-trash";
        color = "#ef4444";
        break;
    }

    return { ...log, icon, color };
  });

  const getActionSeverity = (
    action: string,
  ): "success" | "info" | "warning" | "danger" | null => {
    switch (action) {
      case "CREATE":
        return "success";
      case "UPDATE":
        return "info";
      case "DELETE":
        return "danger";
      default:
        return null;
    }
  };

  const formatSource = (source?: string) => {
    if (!source) return "Unknown";
    return source.charAt(0).toUpperCase() + source.slice(1).replace(/_/g, " ");
  };

  const getCreatedByName = (item: GetActivityLogDto) => {
    const parts = [
      item.created_first_name,
      item.created_middle_name,
      item.created_last_name,
    ].filter(Boolean);
    return parts.join(" ") || "System";
  };

  const isUUID = (value: unknown): boolean => {
    if (typeof value !== "string") return false;
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
  };

  const formatDateValue = (value: unknown): string => {
    if (!value || value === null || value === undefined) return "-";
    const strValue = String(value);
    // Check if it's an ISO date string
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    if (isoDateRegex.test(strValue)) {
      const date = new Date(strValue);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return strValue;
  };

  const formatPaymentAllocations = (
    allocations: {
      payment_code?: string;
      billing?: string;
      amount_allocated?: number;
    }[],
  ): string => {
    if (!Array.isArray(allocations) || allocations.length === 0) return "-";
    return allocations
      .map(
        (a) =>
          `Billing: ${a.billing || "-"}, Amount: ${a.amount_allocated ?? "-"}`,
      )
      .join("\n");
  };

  const parseChanges = (oldValue?: string, newValue?: string) => {
    if (!oldValue && !newValue) return null;

    try {
      const oldObj = oldValue ? JSON.parse(oldValue) : {};
      const newObj = newValue ? JSON.parse(newValue) : {};

      const changes: { field: string; oldVal: string; newVal: string }[] = [];
      const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

      const isBulkPayment =
        newObj.bulk_payment === true || oldObj.bulk_payment === true;

      // Check if this is a payment record
      const isPayment =
        newObj.payment_allocations !== undefined ||
        oldObj.payment_allocations !== undefined ||
        newObj.payment_date !== undefined ||
        oldObj.payment_date !== undefined ||
        newObj.bulk_payment !== undefined ||
        oldObj.bulk_payment !== undefined;

      const excludeFields = [
        "created_at",
        "updated_at",
        "created_by",
        "updated_by",
        "deleted_at",
        "deleted_by",
        "id",
        "code",
        "is_active",
        "version",
        // Exclude amount_paid when bulk_payment is true
        ...(isBulkPayment ? ["amount_paid"] : []),
      ];

      allKeys.forEach((key) => {
        if (excludeFields.includes(key)) return;

        const oldVal = oldObj[key];
        const newVal = newObj[key];

        // Skip status field with "UNPAID" value for payment records
        if (key === "status" && isPayment) {
          if (oldVal === "UNPAID" || newVal === "UNPAID") {
            return;
          }
        }

        // Skip if both values are null/undefined or empty
        if (
          (oldVal === null || oldVal === undefined || oldVal === "") &&
          (newVal === null || newVal === undefined || newVal === "")
        ) {
          return;
        }

        // Skip if either value is a UUID
        if (isUUID(oldVal) || isUUID(newVal)) {
          return;
        }

        // Handle payment_allocations - show only billing and amount_allocated
        if (key === "payment_allocations") {
          const oldFormatted = Array.isArray(oldVal)
            ? formatPaymentAllocations(oldVal)
            : "-";
          const newFormatted = Array.isArray(newVal)
            ? formatPaymentAllocations(newVal)
            : "-";
          if (oldFormatted !== newFormatted) {
            changes.push({
              field: "payment allocations",
              oldVal: oldFormatted,
              newVal: newFormatted,
            });
          }
          return;
        }

        // Handle payment_date - format as readable date
        if (key === "payment_date") {
          const oldFormatted = formatDateValue(oldVal);
          const newFormatted = formatDateValue(newVal);
          if (oldFormatted !== newFormatted) {
            changes.push({
              field: "payment date",
              oldVal: oldFormatted,
              newVal: newFormatted,
            });
          }
          return;
        }

        // Rename actual_total_amount_paid to "Amount Paid" when bulk_payment is true
        if (key === "actual_total_amount_paid" && isBulkPayment) {
          if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes.push({
              field: "Amount Paid",
              oldVal:
                oldVal !== null && oldVal !== undefined ? String(oldVal) : "-",
              newVal:
                newVal !== null && newVal !== undefined ? String(newVal) : "-",
            });
          }
          return;
        }

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          changes.push({
            field: key.replace(/_/g, " "),
            oldVal:
              oldVal !== null && oldVal !== undefined ? String(oldVal) : "-",
            newVal:
              newVal !== null && newVal !== undefined ? String(newVal) : "-",
          });
        }
      });

      return changes.length > 0 ? changes : null;
    } catch {
      return null;
    }
  };

  const renderMultiLineValue = (
    value: string,
    isExpanded: boolean,
    maxLines: number = 3,
  ) => {
    const lines = value.split("\n");
    const shouldTruncate = lines.length > maxLines && !isExpanded;
    const displayLines = shouldTruncate ? lines.slice(0, maxLines) : lines;

    return {
      content: displayLines.map((line, i) => (
        <span key={i}>
          {line}
          {i < displayLines.length - 1 && <br />}
        </span>
      )),
      hasMore: lines.length > maxLines,
      remainingCount: lines.length - maxLines,
    };
  };

  const ChangesSection = ({
    changes,
  }: {
    changes: { field: string; oldVal: string; newVal: string }[];
  }) => {
    const [expanded, setExpanded] = useState(false);
    const [expandedAllocations, setExpandedAllocations] = useState<
      Record<number, boolean>
    >({});
    const displayedChanges = expanded ? changes : changes.slice(0, 5);
    const hasMore = changes.length > 5;

    const toggleAllocationExpand = (idx: number) => {
      setExpandedAllocations((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    return (
      <div className="timeline-changes">
        <span className="changes-title">Changes:</span>
        {displayedChanges.map((change, idx) => {
          const isMultiLine =
            change.oldVal.includes("\n") || change.newVal.includes("\n");
          const isAllocExpanded = expandedAllocations[idx] || false;
          const hasOldValue = change.oldVal && change.oldVal !== "-";
          const hasNewValue = change.newVal && change.newVal !== "-";

          if (isMultiLine) {
            const oldRendered = hasOldValue
              ? renderMultiLineValue(change.oldVal, isAllocExpanded)
              : null;
            const newRendered = hasNewValue
              ? renderMultiLineValue(change.newVal, isAllocExpanded)
              : null;
            const showExpandOption =
              oldRendered?.hasMore || newRendered?.hasMore;

            return (
              <div key={idx} className="change-item change-item-multiline">
                <span className="change-field">{change.field}:</span>
                <div className="change-multiline-content">
                  {hasOldValue && oldRendered && (
                    <div className="change-block-wrapper">
                      <span className="change-block-label">Previous:</span>
                      <div className="change-old-block">
                        {oldRendered.content}
                      </div>
                    </div>
                  )}
                  {hasOldValue && hasNewValue && (
                    <span className="change-arrow-vertical">→</span>
                  )}
                  {hasNewValue && newRendered && (
                    <div className="change-block-wrapper">
                      <span className="change-block-label">
                        {hasOldValue ? "Updated:" : "New:"}
                      </span>
                      <div className="change-new-block">
                        {newRendered.content}
                      </div>
                    </div>
                  )}
                </div>
                {showExpandOption && (
                  <span
                    className="changes-more"
                    onClick={() => toggleAllocationExpand(idx)}
                  >
                    {isAllocExpanded
                      ? "Show less"
                      : `+${Math.max(oldRendered?.remainingCount || 0, newRendered?.remainingCount || 0)} more items`}
                  </span>
                )}
              </div>
            );
          }

          return (
            <div key={idx} className="change-item">
              <span className="change-field">{change.field}:</span>
              {hasOldValue && (
                <span className="change-old">{change.oldVal}</span>
              )}
              {hasOldValue && hasNewValue && (
                <span className="change-arrow">→</span>
              )}
              {hasNewValue && (
                <span className="change-new">{change.newVal}</span>
              )}
              {!hasOldValue && !hasNewValue && (
                <span className="change-empty">-</span>
              )}
            </div>
          );
        })}
        {hasMore && (
          <span className="changes-more" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Show less" : `+${changes.length - 5} more changes`}
          </span>
        )}
      </div>
    );
  };

  const customizedMarker = (item: TimelineEvent) => {
    return (
      <span className="timeline-marker" style={{ backgroundColor: item.color }}>
        <i className={item.icon}></i>
      </span>
    );
  };

  const customizedContent = (item: TimelineEvent) => {
    const changes = parseChanges(item.old_value, item.new_value);

    return (
      <div className="timeline-content">
        <div className="timeline-header">
          <div className="timeline-tags">
            <Tag
              value={item.action}
              severity={getActionSeverity(item.action)}
            />
            <Tag
              value={formatSource(item.request_source)}
              className="source-tag"
            />
          </div>
          <span className="timeline-date">
            {item.created_at && formatDateTime(item.created_at)}
          </span>
        </div>

        <p className="timeline-description">{item.description}</p>

        <span className="timeline-author">By: {getCreatedByName(item)}</span>

        {changes && changes.length > 0 && <ChangesSection changes={changes} />}

        {item.remarks && (
          <div className="timeline-remarks">
            <span>Remarks: {item.remarks}</span>
          </div>
        )}
      </div>
    );
  };

  if (!events || events.length === 0) {
    return null;
  }

  return (
    <div className="activity-timeline">
      <Timeline
        value={events}
        align="left"
        className="customized-timeline"
        marker={customizedMarker}
        content={customizedContent}
      />
    </div>
  );
}
